import React from 'react';

import { ReactComponent as CreatePostSvg } from '../../../assets/create_post.svg';
import { usePlexesMap, useSelectedPlexIds } from '../../../models/filters/useSelectedPlexIds';
import { ButtonColor, PlexButton } from '../../shared/primitives/PlexButton';
import { useDialogState } from '../dialog/useDialogState';
import { canCreatePost } from '../permissions';
import { NewQuestionButtonContainer } from './NewQuestionButton';

interface Props {
  className?: string;
  type: 'post' | 'event';
}

export const NewPostButton = React.memo(({ className, type }: Props): React.ReactElement | null => {
  const dialogStateType = type === 'post' ? 'new_post' : 'new_event';
  const label = type === 'post' ? 'Create Post' : 'Create Event';
  const [, setDialogState] = useDialogState();
  const plexIds = useSelectedPlexIds();
  const plexesMap = usePlexesMap();
  const handleClickOpen = (): void => {
    setDialogState({ type: dialogStateType, plexId: plexIds[0] });
  };

  if (plexIds.length > 1) {
    return (
      <PlexButton
        disabled
        className={className}
        onClick={handleClickOpen}
        label={label}
        SvgIconRight={CreatePostSvg}
        color={ButtonColor.Primary}
        tooltip={'You have selected more than 1 plex'}
      />
    );
  }

  if (plexIds.length === 0) {
    // we can get this case when there is no plex created
    return (
      <PlexButton
        disabled
        className={className}
        onClick={handleClickOpen}
        label={label}
        SvgIconRight={CreatePostSvg}
        color={ButtonColor.Primary}
        tooltip={'You must have at least 1 plex selected'}
      />
    );
  }

  const plex = plexesMap[plexIds[0]];
  if (plex == null) {
    return null;
  }

  // if the user does not have permission to create posts, then show the "ask a question" button
  const userRelation = plex.currentUserRelation.state;
  if (!userRelation || !canCreatePost(userRelation)) {
    return <NewQuestionButtonContainer className={className} boardId={plex.defaultBoardId} />;
  }

  return (
    <PlexButton
      className={className}
      onClick={handleClickOpen}
      label={label}
      SvgIconRight={CreatePostSvg}
      color={ButtonColor.Primary}
    />
  );
});
